import { ref, triggerRef } from 'vue'
import moment from 'moment'

import { defineStore } from 'pinia'
import Platform from 'quasar/src/plugins/Platform.js';

import router, { menuRoutesEnum } from '@/router'
import i18n from '@/plugins/i18n'
import { getLocalizedItems } from '@/plugins/i18n'
import { resetStores } from '@/plugins/pinia'
import { getTranslatedItems } from '@/plugins/i18n'
import { useGetOrFetchAsync } from '@/composables/store'
import { useClaimService } from '@/services/claim.service'

import { webSiteApi } from '@/apis/web-site.api'
import BaseApi from '@/apis/base.api'
import { setAuthorizationToken, setUserDataFilterHeader } from '@/apis/http.api'

import { useWebStreamWs } from '@/ws/web-stream.ws'

import { useRegistryStore } from './registry.store'
import { menuRoutes } from '@/router'
import { useCatalog } from '@/composables/catalog'
import { useNotify } from '@/composables/notify'

// Listener to immediately logout the user if the token is not present in the local storage
window.addEventListener('storage', async(event) => {
  if (event.key === 'web_site') {
    const webStore = JSON.parse(localStorage.getItem('web_site'))
    const store = useWebSiteStore()
    if (!webStore?.accessToken || !webStore?.refreshToken) {
      await store.logoutAsync()
    } else if (webStore.accessToken !== store.accessToken || webStore.refreshToken !== store.refreshToken) {
      store.updateToken(webStore.accessToken, webStore.refreshToken)
      setAuthorizationToken(webStore.accessToken)
      await store.startRefreshTokenTimerAsync()
    }
  }
})

export const useWebSiteStore = defineStore('web_site', () => {
  const api = webSiteApi
  const registryStore = useRegistryStore()
  const catalog = useCatalog()

  const platform = Platform

  const accessToken = ref(null)
  const refreshToken = ref(null)
  const refreshTokenLock = ref(null)
  const refreshTokenTimeout = ref(null)
  const decodedToken = ref(null)
  const decodedPermissions = ref(null)

  const lastUpdates = ref(null)
  const lastUpdatesTimestamp = ref(null)
  const lastUpdateDictionary = ref({})

  const cities = ref([])
  const claimsMap = ref({})
  const clipboardTypes = ref([])
  const countries = ref([])
  const currencies = ref([])
  const defaultApi = new BaseApi('')
  const sideBarExpanded = ref(false)

  const paidColor = '#4caf50'
  const withVATColor = '#4caf50'
  const withoutVATColor = '#ffb300'
  const blankCaption = '\u00A0'

  const storageColors = Object.freeze({
    usedOk: '#43a047',
    usedWarning: '#fb8c00',
    usedDanger: '#e53935',
    remaining: '#cacaca',
  })

  const kanbanCollapsedStatuses = ref([])

  const documentRowTypes = [
    { id: 0, name: 'common.documentRowTypes.product' },
    { id: 1, name: 'common.documentRowTypes.free' },
    { id: 2, name: 'common.documentRowTypes.text' },
    { id: 3, name: 'common.documentRowTypes.vehicle' },
    { id: 4, name: 'common.documentRowTypes.labor' },
    { id: 5, name: 'common.documentRowTypes.expense' },
    { id: 6, name: 'common.documentRowTypes.service' },
  ]
  const documentRowTypesEnum = Object.freeze({
    PRODUCT: 0,
    FREE: 1,
    TEXT: 2,
    VEHICLE: 3,
    LABOR: 4,
    EXPENSE: 5,
    SERVICE: 6,
  })

  const mappingDocumentRowTypeToRegistryProductType = Object.freeze({
    [documentRowTypesEnum.PRODUCT]: registryStore.productTypes.PRODUCT,
    [documentRowTypesEnum.TEXT]: registryStore.productTypes.TEXT,
    [documentRowTypesEnum.LABOR]: registryStore.productTypes.LABOR,
    [documentRowTypesEnum.EXPENSE]: registryStore.productTypes.EXPENSE,
    [documentRowTypesEnum.SERVICE]: registryStore.productTypes.SERVICE,
  })

  const documentRowActions = [
    { id: 0, name: 'common.cancel' },
    { id: 1, name: 'common.add' },
  ]

  const gridEditItemId = ref(null)
  const gridStates = ref({})
  const gridLayoutConfigurations = ref([])

  const importRowTypesEnum = Object.freeze({
    CATALOG: 1,
    LRW: 2,
    CLIPBOARD: 3,
    AUTODATA: 4,
  })

  const importRowSourceTypesEnum = Object.freeze({
    LABOR: 1,
    REPLACEMENT: 2,
    LUBRICANT: 3,
    CONTROL: 4,
    PRICE: 5,
  })

  const importRowSourceTypeMapping = Object.freeze(
    [importRowTypesEnum.CATALOG, importRowTypesEnum.LRW, importRowTypesEnum.CLIPBOARD, importRowTypesEnum.AUTODATA]
    .reduce((acc, key) => {
      acc[key] = importRowSourceTypesEnum.REPLACEMENT
      return acc
    }, {})
  )

  const platformCreditsType = Object.freeze({
    PLATE: 'IB',
    LRW: 'LR',
    SMS: 'NE',
    ELECTRONIC_INVOICE: 'FE',
    ELECTRONIC_INVOICE_XML: 'XM',
  })

  const claimsEnum = Object.freeze({
    APP_MAW_PARTS: 'app.maw.parts',
    APP_MAW_SERVICE: 'app.maw.service',
    CATALOG_LRW_CATALOG: 'maw.lrw.read',
    AUTODATA_READ: 'epc.autodata.read',
    REGISTRY_PRODUCT_READ: 'maw.registry.items.read',
    REGISTRY_PRODUCT_WRITE: 'maw.registry.items.write',
    REGISTRY_DISCOUNT_CATEGORY_READ: 'maw.registry.discountclass.read',
    REGISTRY_SUBJECT_DISCOUNT_READ: 'maw.registry.discountclass.read',
    REGISTRY_SUBJECT_READ: 'maw.registry.buspartners.read',
    REGISTRY_SUPPLIER_BRAND_READ: 'maw.registry.brands.read',
    REGISTRY_DOCUMENT_SERIES_READ: 'maw.registry.document.series.read',
    REGISTRY_DOCUMENT_SERIES_WRITE: 'maw.registry.document.series.write',
    REGISTRY_MERGE_SUPPLIER_BRANDS: 'maw.registry.code.renaming.sw',
    SALE_CLIPBOARD_READ: 'maw.sales.clipboard.read',
    SALE_DIRECT_READ: 'maw.sales.directs.read',
    SALE_INVOICE_READ: 'maw.sales.invoices.read',
    SALE_COMMERCIAL_READ: 'maw.sales.commercialdoc.read',
    SALE_INVOICE_ELECTRONIC: 'maw.sales.invoices.electronic',
    SALE_INVOICE_ELECTRONIC_TRANSMISSION: 'maw.sales.invoices.electronic.sdi',
    SUPPLIER_ORDER_READ: 'maw.orders.suppliers.read',
    WORKSHOP_QUOTE_READ: 'maw.workshop.estimates.read',
    WORKSHOP_WORKSHEET_READ: 'maw.workshop.odl.read',
    WORKSHOP_SERVICE_HISTORY_READ: 'maw.workshop.service_history.read',
    WORKSHOP_KANBAN_READ: 'maw.workshop.service_board.read',
    WORKSHOP_SCHEDULER_READ: 'maw.workshop.scheduler.read',
    VEHICLE_REGISTRY_READ: 'maw.vehicles.circulating_park.read',
    ACCOUNTING_READ: 'maw.contabilita.read',
  })

  const menuEntries = ref([
    {
      code: 'menu.home',
      icon: 'mdi-home',
      order: 1,
    },
    {
      code: 'menu.registry',
      icon: 'mdi-clipboard-list-outline',
      claim: claimsEnum.REGISTRY_READ,
      order: 2,
    },
    {
      code: 'menu.product',
      icon: 'mdi-format-list-bulleted',
      parent_ref: 'menu.registry',
      claim: claimsEnum.REGISTRY_PRODUCT_READ,
      order: 1,
    },
    {
      code: 'menu.discount-category',
      icon: 'mdi-sale',
      parent_ref: 'menu.registry',
      claim: claimsEnum.REGISTRY_DISCOUNT_CATEGORY_READ,
      order: 2,
    },
    {
      code: 'menu.subject-discount',
      icon: 'mdi-label-percent-outline',
      parent_ref: 'menu.registry',
      claim: claimsEnum.REGISTRY_SUBJECT_DISCOUNT_READ,
      order: 2,
    },
    {
      code: 'menu.subject',
      icon: 'mdi-account-circle',
      parent_ref: 'menu.registry',
      claim: claimsEnum.REGISTRY_SUBJECT_READ,
      order: 3,
    },
    {
      code: 'menu.mechanic',
      icon: 'mdi-hammer-screwdriver',
      parent_ref: 'menu.registry',
      claim: claimsEnum.WORKSHOP_SCHEDULER_READ,
      order: 4,
    },
    {
      code: 'menu.supplier-brand',
      icon: 'mdi-tag-text-outline',
      parent_ref: 'menu.registry',
      claim: claimsEnum.REGISTRY_SUPPLIER_BRAND_READ,
      order: 5,
    },
    // {
    //   code: 'menu.document-series',
    //   icon: 'mdi-file-cog-outline',
    //   parent_ref: 'menu.registry',
    //   claim: claimsEnum.REGISTRY_DOCUMENT_SERIES_READ,
    //   order: 6,
    // }, US: 1241, waiting 979
    // {
    //   code: 'menu.table',
    //   icon: 'mdi-table-large',
    //   parent_ref: 'menu.registry',
    //   claim: claimsEnum.MAW_REGISTRY_READ,
    //   url: window.config.ADMIN_PATH || '/admin/',
    //   order: 5,
    // },
    {
      code: 'menu.sale',
      icon: 'mdi-cash-multiple',
      order: 3,
    },
    {
      code: 'menu.sale.clipboard',
      icon: 'mdi-clipboard-text',
      parent_ref: 'menu.sale',
      claim: claimsEnum.SALE_CLIPBOARD_READ,
      order: 1,
    },
    {
      code: 'menu.sale.direct',
      icon: 'mdi-file-document-outline',
      parent_ref: 'menu.sale',
      claim: claimsEnum.SALE_DIRECT_READ,
      order: 2,
    },
    {
      code: 'menu.sale.invoice',
      icon: 'mdi-receipt-text-outline',
      parent_ref: 'menu.sale',
      claim: claimsEnum.SALE_INVOICE_READ,
      order: 3,
    },
    {
      code: 'menu.sale.commercial',
      icon: 'mdi-truck-delivery-outline',
      parent_ref: 'menu.sale',
      claim: claimsEnum.SALE_COMMERCIAL_READ,
      order: 4,
    },
    {
      code: 'menu.sale.electronic-invoice',
      icon: 'mdi-file-send-outline',
      parent_ref: 'menu.sale',
      claim: claimsEnum.SALE_INVOICE_ELECTRONIC,
      order: 5,
    },
    {
      code: 'menu.order',
      icon: 'mdi-cart-outline',
      claim: claimsEnum.SUPPLIER_ORDER_READ,
      order: 4,
    },
    {
      code: 'menu.order.supplier',
      icon: 'mdi-package-variant',
      parent_ref: 'menu.order',
      claim: claimsEnum.SUPPLIER_ORDER_READ,
      order: 1,
    },
    {
      code: 'menu.garage',
      icon: 'mdi-garage-variant',
      claim: claimsEnum.WORKSHOP_QUOTE_READ,
      order: 5,
    },
    {
      code: 'menu.garage.quote',
      icon: 'mdi-calculator',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_QUOTE_READ,
      order: 1,
    },
    {
      code: 'menu.garage.worksheet',
      icon: 'mdi-wrench',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_WORKSHEET_READ,
      order: 2,
    },
    {
      code: 'menu.garage.service-history',
      icon: 'mdi-wrench-clock',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_SERVICE_HISTORY_READ,
      order: 3,
    },
    {
      code: 'menu.garage.worksheet-kanban-board',
      icon: 'mdi-view-dashboard',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_KANBAN_READ,
      order: 4,
    },
    {
      code: 'menu.garage.event-scheduler',
      icon: 'mdi-calendar',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_SCHEDULER_READ,
      order: 5,
    },
    {
      code: 'menu.garage.work-time-entry',
      icon: 'mdi-calendar-clock',
      parent_ref: 'menu.garage',
      claim: claimsEnum.WORKSHOP_SCHEDULER_READ,
      order: 6,
    },
    {
      code: 'menu.vehicle',
      icon: 'mdi-atv',
      claim: claimsEnum.VEHICLE_REGISTRY_READ,
      order: 6,
    },
    {
      code: 'menu.vehicle.registry',
      icon: 'mdi-clipboard-list-outline',
      parent_ref: 'menu.vehicle',
      claim: claimsEnum.VEHICLE_REGISTRY_READ,
      order: 1,
    },
    {
      code: 'menu.service',
      icon: 'mdi-tools',
      order: 7,
    },
    {
      code: 'menu.service.attachment',
      icon: 'mdi-paperclip',
      parent_ref: 'menu.service',
      order: 1,
    },
    {
      code: 'menu.notification',
      icon: 'mdi-bell',
      order: 8,
    },
    {
      code: 'menu.catalogs',
      icon: 'mdi-view-grid',
      order: 8,
    },
    {
      code: 'menu.catalog',
      customIcon: new URL('@/assets/logo-catalog.svg', import.meta.url),
      parent_ref: 'menu.catalogs',
      action: catalog.onCatalogLinkClick,
      order: 1,
    },
    {
      code: 'menu.catalog-lrw',
      icon: 'mdi-archive-outline',
      parent_ref: 'menu.catalogs',
      action: catalog.onCatalogLrwLinkClick,
      order: 2,
    },
    {
      code: 'menu.livecare',
      icon: 'mdi-comment-question-outline',
      order: 10,
      action: () => {
        window.open('https://www.livecare.it/it/download-cliente.php', '_blank')
      },
    },
  ])

  const documentRowTypeButtons = [
    { icon: 'mdi-format-list-bulleted', type: documentRowTypesEnum.PRODUCT },
    { icon: 'mdi-hammer-screwdriver', type: documentRowTypesEnum.LABOR },
    { icon: 'mdi-cash-multiple', type: documentRowTypesEnum.EXPENSE },
    { icon: 'mdi-account-cog', type: documentRowTypesEnum.SERVICE },
    { icon: 'mdi-lock-open-variant', type: documentRowTypesEnum.FREE },
    { icon: 'mdi-lead-pencil', type: documentRowTypesEnum.TEXT },
  ]

  const notificationTypes = [
    { id: 0, name: 'common.info' },
    { id: 1, name: 'common.transmission' },
    { id: 2, name: 'common.print' },
    { id: 3, name: 'common.entityLockRequest' },
    { id: 4, name: 'common.entityLockRelease' },
  ]

  const notificationTypesEnum = Object.freeze({
    INFO: 0,
    TRANSMISSION: 1,
    PRINT: 2,
    ENTITY_LOCK_REQUEST: 3,
    ENTITY_LOCK_RELEASE_CONFIRMATION: 4,
  })

  const weekDaysEnum = Object.freeze({
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
  })

  const agendaColoredByEnum = Object.freeze({
    INTERVENTION_TYPE: 0,
    MECHANIC: 1
  })

  const unitOfMeasureOfTimeEnum = Object.freeze({
    MN: 'MN',
    HR: 'HR',
    UL: 'UL'
  })

  const importRowControlEnum = Object.freeze({
    PRODUCT_ALREADY_EXISTS: 1,
    PRODUCT_NOT_FOUND: 2,
    PRODUCT_HAS_SUPERSESSION: 3,
    QUANTITY_NOT_MULTIPLE_OF_PACKAGE: 4,
    MINIMUM_QUANTITY: 5,
    PRODUCT_NOT_AVAILABLE: 6,
    MISSING_UNIT_OF_MEASURE: 7,
    BRAND_NOT_ALLOWED: 9,
    PRODUCT_FAMILY_NOT_ALLOWED: 10,
  })

  const importRowActionEnum = Object.freeze({
    CANCEL: 0,
    ADD: 1,
    ADD_QUANTITY: 2,
    CREATE_PRODUCT: 3,
  })

  const importRowControlActionMapping = Object.freeze({
    [importRowControlEnum.PRODUCT_ALREADY_EXISTS]: {
      uiType: 'select',
      actions: [
      importRowActionEnum.CANCEL,
      importRowActionEnum.ADD_QUANTITY,
      importRowActionEnum.ADD,
      ],
    },
    [importRowControlEnum.PRODUCT_NOT_FOUND]: {
      uiType: 'select',
      actions: [
      importRowActionEnum.ADD,
      importRowActionEnum.CREATE_PRODUCT,
      ],
    },
    [importRowControlEnum.PRODUCT_HAS_SUPERSESSION]: {
      uiType: 'supersession',
      actions: [
        importRowActionEnum.ADD,
      ],
    },
    [importRowControlEnum.QUANTITY_NOT_MULTIPLE_OF_PACKAGE]: {
      uiType: 'package',
      actions: [
        importRowActionEnum.ADD,
      ],
    },
    [importRowControlEnum.MINIMUM_QUANTITY]: {
      uiType: 'minimum',
      actions: [
        importRowActionEnum.ADD,
      ],
    },
    [importRowControlEnum.PRODUCT_NOT_AVAILABLE]: {
      uiType: 'not_available',
      actions: [
        importRowActionEnum.ADD,
      ],
    },
    [importRowControlEnum.MISSING_UNIT_OF_MEASURE]: {
      uiType: 'missing_unit_measure',
      actions: [
        importRowActionEnum.ADD,
      ],
    },
  })

  const interventionTypeCodeEnum = Object.freeze({
    WARRANTY: 'G',
    CAR_SERVICE: 'T',
  })

  const saleDepartmentCodeEnum = Object.freeze({
    SHOP: 'N',
    WORKSHOP: 'O',
  })

  const printHeaderOptionEnum = Object.freeze({
    NO_HEADER: 2 ** 0,
    COMPANY_DATA: 2 ** 1,
    LOGO: 2 ** 2
  })

  const isUserActive = ref(false)
  const userActivityTimer = ref(null)
  const currentlyLockedEntity = ref(null)

  const defaultAgendaColoredBy = ref(0)
  const notificationsCount = ref(0)
  const entityLocks = ref({})
  const paymentTypes = ref([])
  const paymentEffectTypes = ref([])
  const printSettings = ref([])
  const productFamilies = ref([])
  const sideBarPinned = ref(false)
  const splitterStates = ref({})
  const states = ref([])
  const unitConversions = ref({})
  const unitsOfMeasure = ref([])
  const urgencyTypes = ref([])
  const userDetails = ref([])
  const user = ref(null)
  const userDataFilter = ref(false)
  const vatTypes = ref([])
  const allowAutoLogin = ref(true)

  const cookieAuthTokenName = 'auth_token'

  // WebSocket
  const webStreamWs = useWebStreamWs()
  const subscriptions = []

  const onEntityLockMessage = (sender, response, message) => {
    // const updated = structuredClone(entityLocks.value)
    const updated = entityLocks.value

    for (const key in message.data) {
      const messageKey = key.replace(/\d+\./, '')
      const messageKeyValue = message.data[key]

      if (messageKeyValue.user_id) {
        updated[messageKey] = message.data[key]
      } else {
        delete updated[messageKey]
      }
    }

    entityLocks.value = updated
    triggerRef(entityLocks)
  }

  const subscribeEntityLockStreamAsync = async() => {
    const subscription = await webStreamWs.subscribeAsync(
      [webStreamWs.ENTITY_LOCK_STREAM],
      onEntityLockMessage,
      onEntityLockMessage,
    )
    subscriptions.push(subscription)
  }

  const acquireRefreshTokenLock = () => (refreshTokenLock.value = new Date().getTime())

  const releaseRefreshTokenLock = () => (refreshTokenLock.value = null)

  const newVehicleOwnerMovement = (customer_id, vehicle_id) => {
    return {
      customer_id: customer_id,
      vehicle: vehicle_id,
      movement_type: registryStore.vehicleMovementTypesEnum.OWNERSHIP_TRANSFER,
      movement_datetime: new Date().toISOString(),
    }
  }

  const emptyCustomer = {
    id: null,
    name: '',
    name_ext: '',
    subject_type_ref: null,
    subject_sub_type_ref: null,
    code: '',
    vat_country: null,
    vat_country_id: null,
    vat_type_ref: null,
    vat_number: '',
    tax_code: '',
    address: {
      street: null,
      city_text: null,
      city: null,
      state: null,
      country: null,
      zip_code: null,
      id: null,
      country_name: null,
      city_name: null,
      state_name: null,
      state_text: null,
      state_code: null,
    },
    email: '',
    mobile: '',
    phone: ''
  }

  const emptyVehicle = {
    id: null,
    name: '',
    color: '',
    plate_number: '',
    vin: '',
    km: 0,
    engine_size: null,
    engine_code: '',
    enrollment_date: null,
    sale_date: null,
    revision_due_date: null,
    next_revision_due_date: null,
    supplier_brand_ref: null,
    fuel_type: null,
  }

  const emptyDeliveryAddress = {
    id: null,
    name: '',
    address: {
      street: '',
      city_text: '',
      city_name: '',
      city: null,
      state: null,
      state_name: '',
      state_code: '',
      country: null,
      country_name: '',
      zip_code: ''
    },
    email: '',
    mobile: '',
    phone: ''
  }

  const onGridEditCanceled = () => {} // Hook for grid edit cancel

  const downloadNotificationAsync = async(id, filename) => webSiteApi.downloadNotificationAsync(id, filename)

  const fetchEntityLastUpdateAsync = async(store = null, getLastUpdateAsync = null, functionNameFn = null, model = null) => {
    const res = await getLastUpdateAsync()
    const currentModel = model || res?.data?.model
    if (currentModel) {
      const index = lastUpdates.value.findIndex(x => x.model === currentModel)
      if (index !== -1) {
        lastUpdates.value[index] = {
          ...lastUpdates.value[index],
          updated_at: moment.utc(res?.data.updated_at).toISOString()
        }
      } else if (model) {
        lastUpdates.value.push({
          model: currentModel,
          updated_at: moment.utc(res?.data.updated_at).toISOString()
        })
      }

      const x = lastUpdates.value.find(x => x.model === currentModel)
      if (x) {
        const modelName = x.model.split('.').pop()
        const lastLocalUpdate = lastUpdateDictionary.value[x.model]
        if (!lastLocalUpdate || lastLocalUpdate !== x.updated_at) {
          const functionName = (functionNameFn && functionNameFn(modelName)) || `fetch${modelName}sAsync`
          await store[functionName](true, { $notop: true })
          lastUpdateDictionary.value[x.model] = x.updated_at
        }
      }
    }
  }

  const fetchLastUpdateAsync = async(force = false) => {
    await useGetOrFetchAsync(lastUpdates, () => webSiteApi.getLastUpdateAsync(), force)
    const portalModels = lastUpdates.value.filter(x => x.model.startsWith('portal') && lastUpdates.value.filter(x => !x.model.startsWith('portal')).some(y => y.model.split('.')[2] === x.model.split('.')[2]))
    lastUpdates.value = lastUpdates.value
      .map(x => {
        x.updated_at = moment.utc(x.updated_at).toISOString()
        return x
      })
      .filter(x => !portalModels.some(y => y.model === x.model)).map(x => {
        const currentModelName = x.model.split('.')[2]
        const portalModel = portalModels.find(x => x.model === `portal.models.${currentModelName}`)
        x.updated_at = moment(x.updated_at || 0).isAfter(portalModel?.updated_at || 0) ? x.updated_at : portalModel?.updated_at
        return x
      })
    return lastUpdates
  }

  const fetchCountriesAsync = async(force = false, params) => useGetOrFetchAsync(countries, () => webSiteApi.getCountriesAsync(params), force)
  const fetchClipboardTypesAsync = async(force = false, params) => useGetOrFetchAsync(clipboardTypes, () => webSiteApi.getClipboardTypesAsync(params), force)
  const fetchCurrenciesAsync = async(force = false, params) => useGetOrFetchAsync(currencies, () => webSiteApi.getCurrenciesAsync(params), force)
  const fetchNotificationDetailsByIdAsync = async(id) => webSiteApi.getNotificationDetailsByIdAsync(id)
  const fetchPaymentTypesAsync = async(force = false, params) => useGetOrFetchAsync(paymentTypes, () => webSiteApi.getPaymentTypesAsync(params), force)
  const fetchPaymentEffectTypesAsync = async(force = false, params) => useGetOrFetchAsync(paymentEffectTypes, () => webSiteApi.getPaymentEffectTypesAsync(params), force)
  const fetchPrintSettingsAsync = async(force = false, params) => useGetOrFetchAsync(printSettings, () => webSiteApi.getPrintSettingsAsync(params), force)
  const fetchStatesAsync = async(force = false, params) => useGetOrFetchAsync(states, () => webSiteApi.getStatesAsync(params), force)
  const fetchUnitConversionsAsync = async(force = false, params) =>
    useGetOrFetchAsync(
      unitConversions,
      async() => {
        const list = (await webSiteApi.getUnitConversionsAsync(params))?.data

        const dict = list.reduce((acc, item) => {
          const from = item.unit_of_measure_from_ref_code
          const to = item.unit_of_measure_to_ref_code
          const ratio = item.ratio

          // forward
          if (!acc[from]) acc[from] = []
          acc[from].push({
            id: item.unit_of_measure_to_ref,
            code: to,
            ratio: ratio,
          })

          // reverse
          if (!acc[to]) acc[to] = []
          acc[to].push({
            id: item.unit_of_measure_from_ref,
            code: from,
            ratio: 1 / ratio,
          })

          return acc
        }, {})

        return {data: dict}
      },
      force
    )
  const fetchUnitsOfMeasureAsync = async(force = false, params) => useGetOrFetchAsync(unitsOfMeasure, () => webSiteApi.getUnitsOfMeasureAsync(params), force)
  const fetchUrgencyTypesAsync = async(force = false, params) => useGetOrFetchAsync(urgencyTypes, () => webSiteApi.getUrgencyTypesAsync(params), force)
  const fetchUserDetailsAsync = async(force = false, params) => useGetOrFetchAsync(userDetails, () => webSiteApi.getUserDetailsAsync(params), force)
  const fetchVatTypesAsync = async(force = false, params) => useGetOrFetchAsync(vatTypes, () => webSiteApi.getVatTypesAsync(params), force)

  const fetchGridLayoutConfigurationsAsync = async(force = false, params = {}) => useGetOrFetchAsync(gridLayoutConfigurations, () => webSiteApi.getGridLayoutConfigurationsAsync({ ...params }), force)

  const getOrFetchGridLayoutConfigurationsAsync = async() => {
    await fetchEntityLastUpdateAsync(useWebSiteStore(), () => webSiteApi.getLastUpdate(webSiteApi.gridLayoutConfigurationsUrl))
    return gridLayoutConfigurations.value
  }

  const getDocumentRowTypeById = (id) => documentRowTypes.find(x => x.id === id)
  const getDefaultDocumentRowType = () => documentRowTypesEnum.PRODUCT
  const getLocalizedCurrencies = () => getTranslatedItems(currencies.value)
  const getLocalizedClipboardTypes = () => getTranslatedItems(clipboardTypes.value)
  const getLocalizedDocumentRowTypes = () => getLocalizedItems(Object.entries(documentRowTypesEnum).map(([key, value]) => {
    const colors = {
      product: '#2196f3', // Blue 500
      text: '#607d8b', // Blue Grey 500
      labor: '#ff9800', // Orange 500
      free: '#9c27b0', // Purple 500
      service: '#4caf50', // Green 500
      expense: '#f44336', // Red 500
    }

    return {
      id: documentRowTypes[value].id,
      name: documentRowTypes[value].name,
      color: colors[key.toLowerCase()],
    }
  }))
  const getLocalizedDocumentRowActions = () => getLocalizedItems(documentRowActions)
  const getLocalizedNotificationTypes = () => getLocalizedItems(notificationTypes)
  const getLocalizedImportRowTypes = () => getLocalizedItems(Object.entries(importRowTypesEnum).map(([key, value]) => {
    const colors = {
      catalog: '#2196f3', // Blue 500
      lrw: '#607d8b', // Blue Grey 500
      clipboard: '#ff9800', // Orange 500
      autodata: '#00897b',
    }

    return {
      id: value,
      name: `common.importRowType.${key.toLowerCase()}`,
      color: colors[key.toLowerCase()],
    }
  }))
  const getLocalizedImportRowSourceTypes = () => getLocalizedItems(Object.entries(importRowSourceTypesEnum).map(([key, value]) => {
    return {
      id: value,
      name: `common.importRowSourceType.${key.toLowerCase()}`,
    }
  }))
  const getLocalizedImportRowValidationMessagesByControl = (control) =>
    getLocalizedItems(Object.entries(importRowControlEnum)
      .filter(([, value]) => value === control)
      .map(([key, value]) => ({
        id: value,
        name: `common.importRowValidation.${key.toLowerCase()}`,
      }))
    )
  const getLocalizedImportRowActionOptions = validationControl => {
    const controlConfig = importRowControlActionMapping[validationControl]
    const actions = controlConfig?.actions || []

    return getLocalizedItems(
      actions.map(actionId => {
        const actionKey = Object.keys(importRowActionEnum).find(
          key => importRowActionEnum[key] === actionId
        )

        return {
          id: actionId,
          name: `common.documentRowImportAction.${actionKey.toLowerCase()}`,
        }
      })
    )
  }
  const getLocalizedPaymentTypes = () => getTranslatedItems(paymentTypes.value)
  const getLocalizedPrintHeaderOptions = () => getLocalizedItems(Object.keys(printHeaderOptionEnum).map(x => ({
    id: printHeaderOptionEnum[x],
    name: `common.printSettings.${x.toLowerCase()}`
  })))
  const getLocalizedWeekDays = () => getLocalizedItems(Object.keys(weekDaysEnum).map(x => ({
    id: weekDaysEnum[x],
    name: `common.week.${x.toLowerCase()}`,
  })))
  const getLocalizedUnitsOfMeasure = () => getTranslatedItems(unitsOfMeasure.value)
  const getLocalizedUrgencyTypes = () => getTranslatedItems(urgencyTypes.value)
  const getLocalizedVatTypes = () => getTranslatedItems(vatTypes.value)

  const getOrFetchUserDetailsAsync = async() => {
    await fetchEntityLastUpdateAsync(useWebSiteStore(), () => webSiteApi.getLastUpdate(webSiteApi.userDetailsUrl))
    return userDetails.value
  }

  const updateImportGridPreferenceAsync = async(groupImportRowsByDefault) => {
    const response = await webSiteApi.updateImportGridPreferenceAsync({
      group_import_rows_by_default: groupImportRowsByDefault,
    })
    const savedUserDetail = response.data
    const index = userDetails.value.findIndex(item => item.id === savedUserDetail.id)

    if (index >= 0) {
      userDetails.value[index] = { ...userDetails.value[index], ...savedUserDetail }
    } else {
      userDetails.value.push(savedUserDetail)
    }

    return savedUserDetail
  }

  const getMenuEntries = () => {
    const claimService = useClaimService()
    return menuEntries.value.filter(x => !x.claim || claimService.hasClaim(x.claim)).map(x => {
      x.route = menuRoutes[x.code]
      return x
    })
  }

  const hasClaim = (claim) => useClaimService().hasClaim(claim)

  const updateNotificationsAsReadAsync = async(data, params) => webSiteApi.updateNotificationsAsReadAsync(data, params)

  const setUserDataFilter = (value) => {
    userDataFilter.value = value
    setUserDataFilterHeader(value)
  }

  const upsertPrintSettingsAsync = async(printSetting) => {
    let action = () => webSiteApi.addPrintSettingsAsync(printSetting)
    if (printSetting.id) {
      action = () => webSiteApi.updatePrintSettingsAsync(printSetting.id, printSetting)
    }
    return action().then(() => fetchPrintSettingsAsync(true))
  }

  const setGridState = (gridName, gridState) => {
    gridStates.value[gridName] = gridState
  }

  const setSplitterState = (splitterName, splitterState) => {
    splitterStates.value[splitterName] = splitterState
  }

  const isValidToken = (token, isRefresh = false) => {
    if (isRefresh) {
      return token && token.access
    }
    return token && token.access && token.refresh
  }

  const processToken = (token) => {
    if (window.config.AUTH_OIDC_ENABLED) {
      token.access = token.access_token
      token.refresh = token.refresh_token
    }
    return token
  }

  const loginAsync = async(credentials) => {
    try {
      if ((window.config.AUTH_OIDC_ENABLED && !credentials?.code) ||
          (!window.config.AUTH_OIDC_ENABLED && (!credentials?.username || !credentials?.password))) {
        return logoutAsync()
      }
      allowAutoLogin.value = false
      const res = window.config.AUTH_OIDC_ENABLED ? await webSiteApi.getTokenOidcAsync(credentials?.code) : await webSiteApi.getTokenAsync(credentials?.username, credentials.password)
      await setTokenAsync(processToken(res.data))
      await subscribeEntityLockStreamAsync()
      return routeToHomeAsync()
    } catch (err) {
      console.error(err)
      return logoutAsync()
    }
  }

  const autoLoginAsync = async() => {
    try {
      allowAutoLogin.value = false
      if (!accessToken.value || !refreshToken.value) {
        return logoutAsync(false)
      }
      if (!isTokenExpired()) {
        await setTokenAsync({ access: accessToken.value, refresh: refreshToken.value })
      } else {
        await refreshTokenAsync()
      }
      await subscribeEntityLockStreamAsync()
    } catch (err) {
      console.error(err)
    }
  }

  const routeToHomeAsync = async() => {
    if (refreshToken.value && accessToken.value) {
      return router.push(menuRoutesEnum.HOME)
    } else {
      return router.push(menuRoutesEnum.LOGIN)
    }
  }

  const logoutActionEnum = Object.freeze({
    LOGOUT: 'Logout',
    ROUTE_HOME: 'RouteHome',
    HREF_AUTH_SERVER: 'HrefAuthServer',
  })

  const isLoggingOut = ref(false)

  const logoutAsync = async(userLogout = true) => {
    if (isLoggingOut.value) return
    isLoggingOut.value = true
    try {
      if (userLogout) {
        const hadSession = !!accessToken.value
        deleteCookie(cookieAuthTokenName)
        resetStores()
        if (window.config.AUTH_OIDC_ENABLED && hadSession) {
          window.location.href = window.config.AUTH_OIDC_LOGOUT_URL
          return
        }
        if (router.currentRoute.value.path !== menuRoutesEnum.LOGIN) {
          return router.push(menuRoutesEnum.LOGIN)
        }
      } else {
        window.location.href = window.config.AUTH_SERVER_URL
      }
    } finally {
      isLoggingOut.value = false
    }
  }

  const isLogged = () => {
    const webStore = JSON.parse(localStorage.getItem('web_site'))
    return webStore && webStore.accessToken && webStore.refreshToken
  }

  const setCookie = (name, value, options = {}) => {
    const opts = { path: '/', secure: location.protocol === 'https:', samesite: 'strict', ...options }
    let cookie = `${name}=${value}`

    if (opts.expires) cookie += `; expires=${opts.expires.toUTCString()}`
    if (opts['max-age']) cookie += `; max-age=${opts['max-age']}`
    if (opts.secure) cookie += `; secure`
    if (opts.samesite) cookie += `; samesite=${opts.samesite}`

    document.cookie = cookie
  }

  const deleteCookie = (name, options = {}) => { setCookie(name, '', { ...options, 'max-age': 0 }) }

  const isTokenExpired = () => decodedToken.value && ((decodedToken.value.exp * 1000) <= (Date.now() + 60 * 1000))

  const refreshTokenAsync = async(retries = 0) => {
    if (!refreshToken.value) {
      return logoutAsync()
    }
    try {
      while (refreshTokenLock.value && Date.now() - refreshTokenLock.value < 5000) {
        console.warn('Waiting for refresh token lock')
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      if (isTokenExpired()) {
        acquireRefreshTokenLock()
        refreshToken.value = JSON.parse(localStorage.getItem('web_site'))?.refreshToken || refreshToken.value
        const res = window.config.AUTH_OIDC_ENABLED ? await webSiteApi.getFreshTokenOidcAsync(refreshToken.value) : await webSiteApi.getFreshTokenAsync(refreshToken.value)
        await setTokenAsync(processToken(res.data), true)
        releaseRefreshTokenLock()
      } else {
        await startRefreshTokenTimerAsync()
      }
    } catch (err) {
      releaseRefreshTokenLock()
      if (retries < 2) {
        await refreshTokenAsync(retries + 1)
      } else {
        console.error(err)
        refreshToken.value = null
        const { notifyError } = useNotify()
        const { t } = i18n.global
        notifyError(t('error.generic_authentication'))
        await logoutAsync()
      }
    }
  }

  const updateToken = (accessTokenValue, refreshTokenValue) => {
    const claimService = useClaimService()
    accessToken.value = accessTokenValue
    refreshToken.value = refreshTokenValue
    decodedToken.value = JSON.parse(atob(accessToken.value.split('.')[1]))
    decodedPermissions.value = claimService.decode(decodedToken.value.permissions)
    setCookie(cookieAuthTokenName, accessTokenValue)
  }

  const setTokenAsync = async(token, isRefresh = false) => {
    if (!isValidToken(token, isRefresh)) {
      return logoutAsync()
    }
    updateToken(token.access, token.refresh || refreshToken.value)

    setAuthorizationToken(accessToken.value)
    if (!isRefresh) await fetchClaimsAsync()
    await startRefreshTokenTimerAsync()
  }

  const startRefreshTokenTimerAsync = async() => {
    // parse json object from base64 encoded jwt token
    await setUserAsync(decodedToken.value)

    clearRefreshTokenTimer()

    // set a timeout to refresh the token a minute before it expires
    const timeout = (decodedToken.value.exp * 1000) - Date.now() - (60 * 1000)
    refreshTokenTimeout.value = setTimeout(refreshTokenAsync, timeout)
  }

  const setUserAsync = async(token) => {
    user.value = {
      id: token.userid,
      username: token.preferred_username?.trim(),
      email: token.email?.trim(),
      name: token.preferred_username?.trim(),
      tenant_code: token.csoft,
      has_electronic_invoice_transmission: hasClaim(claimsEnum.SALE_INVOICE_ELECTRONIC_TRANSMISSION),
      has_catalog_lrw_catalog: hasClaim(claimsEnum.CATALOG_LRW_CATALOG),
      has_autodata_read: hasClaim(claimsEnum.AUTODATA_READ),
    }
  }

  const startUserActivityTracking = () => {
    if (userActivityTimer.value) return

    isUserActive.value = false

    const onUserAction = () => {
      isUserActive.value = true
      window.removeEventListener('mousemove', onUserAction)
      window.removeEventListener('keydown', onUserAction)
    }

    window.addEventListener('mousemove', onUserAction)
    window.addEventListener('keydown', onUserAction)

    const registryStore = useRegistryStore()
    const trackingDuration = registryStore.companyData.entity_lock_duration_minutes

    userActivityTimer.value = setTimeout(async() => {
      window.removeEventListener('mousemove', onUserAction)
      window.removeEventListener('keydown', onUserAction)

      if (isUserActive.value) {
        const entityLock = currentlyLockedEntity.value?.lockedKey?.split('.').slice(1).join('.') || null
        const currentEntityLock = entityLocks.value[entityLock]
        if (currentEntityLock) {
          const url = currentEntityLock.refresh_lock_url
          const cleanedUrl = url.startsWith('/api') ? url.slice(4) : url

          return defaultApi.post(cleanedUrl, {lock: true})
            .finally(() => {
              userActivityTimer.value = null
              startUserActivityTracking()
            })
        }
      } else {
        // notify
        if (trackingDuration > 0) {
          const { notifyWarning } = useNotify()
          const { t } = i18n.global
          notifyWarning(t('warning.entity_lock.entity_released'), {
            timeout: 0,
            actions: [{ icon: 'mdi-close', color: 'white', round: true, handler: () => {} }]
          })
          userActivityTimer.value = null
          isUserActive.value = null
          currentlyLockedEntity.value = null
        }
      }
    }, trackingDuration * 60 * 1000)
  }

  const clearRefreshTokenTimer = () => { clearTimeout(refreshTokenTimeout.value) }

  const clearTokenData = () => {
    accessToken.value = null
    refreshToken.value = null
    decodedToken.value = null
  }

  const getLogo = (horizontal = true) => {
    const appParts = hasClaim(claimsEnum.APP_MAW_PARTS)
    const appService = hasClaim(claimsEnum.APP_MAW_SERVICE)

    if (horizontal) {
      if (appService) {
        return new URL('@/assets/logo-arco-service-horizontal.svg', import.meta.url).href
      }
      if (appParts) {
        return new URL('@/assets/logo-arco-parts-horizontal.svg', import.meta.url).href
      }
    } else {
      if (appService) {
        return new URL('@/assets/logo-arco-service-vertical.svg', import.meta.url).href
      }
      if (appParts) {
        return new URL('@/assets/logo-arco-parts-vertical.svg', import.meta.url).href
      }
    }
    return new URL('@/assets/logo.svg', import.meta.url).href
  }

  const fetchClaimsAsync = async(force = false) => {
    const res = await webSiteApi.getClaims()
    if (res.data) {
      claimsMap.value = res.data.reduce((acc, x) => {
        acc[x.cabilaab] = x.id
        return acc
      }, {})
    }
  }

  const $reset = () => {
    setAuthorizationToken(null)
    clearRefreshTokenTimer()

    accessToken.value = null
    decodedToken.value = null
    refreshToken.value = null
    refreshTokenLock.value = null
    refreshTokenTimeout.value = null
    lastUpdates.value = null
    lastUpdatesTimestamp.value = null
    lastUpdateDictionary.value = {}
    decodedPermissions.value = null

    clipboardTypes.value = []
    countries.value = []
    currencies.value = []

    documentRowTypes.value = []
    splitterStates.value = {}

    defaultAgendaColoredBy.value = 0
    notificationsCount.value = 0
    paymentTypes.value = []
    paymentEffectTypes.value = []
    printSettings.value = []
    productFamilies.value = []
    states.value = []
    unitConversions.value = {}
    unitsOfMeasure.value = []
    urgencyTypes.value = []
    userDetails.value = []
    user.value = null
    userDataFilter.value = false
    vatTypes.value = []
  }

  const getDefaultCurrency = () => {
    const registryStore = useRegistryStore()
    if (registryStore.companyData.value && registryStore.companyData.value.currency) {
      return currencies.value.find(x => x.id === registryStore.companyData.value.currency)
    }
    return currencies.value.find(x => x.code === 'EUR')
  }

  return {
    accessToken,
    agendaColoredByEnum,
    allowAutoLogin,
    api,
    blankCaption,
    cities,
    claimsEnum,
    claimsMap,
    clipboardTypes,
    countries,
    currencies,
    currentlyLockedEntity,
    decodedPermissions,
    decodedToken,
    defaultAgendaColoredBy,
    documentRowActions,
    documentRowTypeButtons,
    documentRowTypes,
    documentRowTypesEnum,
    emptyCustomer,
    emptyDeliveryAddress,
    emptyVehicle,
    entityLocks,
    gridEditItemId,
    gridStates,
    gridLayoutConfigurations,
    importRowTypesEnum,
    importRowActionEnum,
    importRowControlEnum,
    importRowControlActionMapping,
    importRowSourceTypeMapping,
    isUserActive,
    interventionTypeCodeEnum,
    kanbanCollapsedStatuses,
    lastUpdateDictionary,
    lastUpdates,
    lastUpdatesTimestamp,
    logoutActionEnum,
    mappingDocumentRowTypeToRegistryProductType,
    menuEntries,
    notificationsCount,
    notificationTypes,
    notificationTypesEnum,
    paidColor,
    storageColors,
    paymentEffectTypes,
    paymentTypes,
    platform,
    platformCreditsType,
    printSettings,
    printHeaderOptionEnum,
    refreshToken,
    refreshTokenLock,
    refreshTokenTimeout,
    saleDepartmentCodeEnum,
    sideBarExpanded,
    sideBarPinned,
    splitterStates,
    states,
    unitConversions,
    unitsOfMeasure,
    unitOfMeasureOfTimeEnum,
    urgencyTypes,
    user,
    userActivityTimer,
    userDataFilter,
    userDetails,
    vatTypes,
    weekDaysEnum,
    withVATColor,
    withoutVATColor,
    $reset,
    autoLoginAsync,
    clearTokenData,
    downloadNotificationAsync,
    fetchClaimsAsync,
    fetchClipboardTypesAsync,
    fetchCountriesAsync,
    fetchCurrenciesAsync,
    fetchEntityLastUpdateAsync,
    fetchLastUpdateAsync,
    fetchNotificationDetailsByIdAsync,
    fetchPaymentEffectTypesAsync,
    fetchPaymentTypesAsync,
    fetchPrintSettingsAsync,
    fetchGridLayoutConfigurationsAsync,
    fetchStatesAsync,
    fetchUnitConversionsAsync,
    fetchUnitsOfMeasureAsync,
    fetchUrgencyTypesAsync,
    fetchUserDetailsAsync,
    fetchVatTypesAsync,
    getDefaultCurrency,
    getDefaultDocumentRowType,
    getLocalizedImportRowTypes,
    getLocalizedImportRowSourceTypes,
    getDocumentRowTypeById,
    getLocalizedClipboardTypes,
    getLocalizedCurrencies,
    getLocalizedDocumentRowActions,
    getLocalizedDocumentRowTypes,
    getLocalizedImportRowActionOptions,
    getLocalizedImportRowValidationMessagesByControl,
    getLocalizedNotificationTypes,
    getLocalizedPaymentTypes,
    getLocalizedPrintHeaderOptions,
    getLocalizedWeekDays,
    getLocalizedUnitsOfMeasure,
    getLocalizedUrgencyTypes,
    getLocalizedVatTypes,
    getOrFetchUserDetailsAsync,
    getOrFetchGridLayoutConfigurationsAsync,
    getLogo,
    getMenuEntries,
    hasClaim,
    isLogged,
    isValidToken,
    loginAsync,
    logoutAsync,
    newVehicleOwnerMovement,
    onGridEditCanceled,
    refreshTokenAsync,
    setGridState,
    setSplitterState,
    setUserDataFilter,
    startUserActivityTracking,
    startRefreshTokenTimerAsync,
    stopRefreshTokenTimer: clearRefreshTokenTimer,
    updateNotificationsAsReadAsync,
    updateImportGridPreferenceAsync,
    updateToken,
    upsertPrintSettingsAsync,
  }
}, {
  persist: [
    {
      debug: true,
      pick:
        [
          'accessToken',
          'refreshToken',
          'refreshTokenLock',
          'decodedToken',
          'defaultAgendaColoredBy',
          'claimsMap',
          'clipboardTypes',
          'countries',
          'currencies',
          'documentRowTypes',
          'editingMode',
          'gridStates',
          'lastUpdateDictionary',
          'paymentEffectTypes',
          'paymentTypes',
          'printSettings',
          'sideBarPinned',
          'splitterStates',
          'states',
          'supplierDivisionTypes',
          'supplierOrderTypes',
          'supplierTypes',
          'unitConversions',
          'gridLayoutConfigurations',
          'unitsOfMeasure',
          'urgencyTypes',
          'userDetails',
          'lastUpdates',
          'lastUpdatesTimestamp',
          'userDataFilter',
          'vatTypes',
          'kanbanCollapsedStatuses',
        ],
      storage: localStorage,
    },
    {
      pick: ['gridEditItemId'],
      storage: sessionStorage,
    }
  ]
})
